// src/pages/Dashboard/Admin/DepensesRecettesDashboard/components/AccountingCharts.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const MONTHS_FR = [
  "janv.",
  "févr.",
  "mars",
  "avr.",
  "mai",
  "juin",
  "juil.",
  "août",
  "sept.",
  "oct.",
  "nov.",
  "déc.",
];

const BUDGET_ORDER = [6, 7, 8, 9, 10, 11, 0, 1, 2, 3, 4, 5];

const PIE_COLORS = [
  "#6366F1",
  "#8B5CF6",
  "#EC4899",
  "#F59E0B",
  "#10B981",
  "#3B82F6",
  "#F43F5E",
  "#22C55E",
  "#F97316",
  "#06B6D4",
];

const fmtEUR = (value, maximumFractionDigits = 0) =>
  Number(value || 0).toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits,
  });

function useIsDark() {
  const [isDark, setIsDark] = useState(() =>
    typeof document !== "undefined"
      ? document.documentElement.classList.contains("dark")
      : false
  );

  useEffect(() => {
    if (typeof MutationObserver === "undefined") return undefined;

    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  return isDark;
}

export default function AccountingCharts({ rows = [], saison }) {
  const isDark = useIsDark();

  const monthly = useMemo(() => {
    const base = Array.from({ length: 12 }, (_, monthIndex) => ({
      monthIndex,
      month: MONTHS_FR[monthIndex],
      depense: 0,
      recette: 0,
    }));

    rows.forEach((row) => {
      if (!row?.date_operation) return;

      const date = new Date(row.date_operation);
      if (Number.isNaN(date.getTime())) return;

      const monthIndex = date.getMonth();
      const target = base[monthIndex];

      if (!target) return;

      if (row.type_operation === "Dépense") {
        target.depense += Number(row.montant || 0);
      }

      if (row.type_operation === "Recette") {
        target.recette += Number(row.montant || 0);
      }
    });

    const ordered = saison
      ? BUDGET_ORDER.map((monthIndex) =>
          base.find((item) => item.monthIndex === monthIndex)
        )
      : base
          .filter((item) => item.depense + item.recette > 0)
          .sort((a, b) => a.monthIndex - b.monthIndex);

    const source = ordered.length ? ordered : base;

    let cumulative = 0;

    return source.map((item) => {
      cumulative += item.recette - item.depense;

      return {
        label: item.month,
        depense: Math.round(item.depense),
        recette: Math.round(item.recette),
        solde: Math.round(cumulative),
      };
    });
  }, [rows, saison]);

  const { pieDepenses, pieRecettes } = useMemo(() => {
    const depenses = new Map();
    const recettes = new Map();

    rows.forEach((row) => {
      const amount = Number(row?.montant || 0);
      if (!amount) return;

      const category = row?.categorie || "Non renseigné";

      if (row?.type_operation === "Dépense") {
        depenses.set(category, (depenses.get(category) || 0) + amount);
      }

      if (row?.type_operation === "Recette") {
        recettes.set(category, (recettes.get(category) || 0) + amount);
      }
    });

    return {
      pieDepenses: Array.from(depenses, ([name, value]) => ({
        name,
        value,
      })).sort((a, b) => b.value - a.value),
      pieRecettes: Array.from(recettes, ([name, value]) => ({
        name,
        value,
      })).sort((a, b) => b.value - a.value),
    };
  }, [rows]);

  const grid = isDark ? "#334155" : "#e2e8f0";
  const tick = isDark ? "#cbd5e1" : "#334155";
  const cursorFill = isDark
    ? "rgba(148,163,184,0.12)"
    : "rgba(148,163,184,0.15)";

  const colors = {
    depTop: "#FB7185",
    depBottom: isDark ? "#E11D48" : "#F43F5E",
    recTop: "#34D399",
    recBottom: isDark ? "#059669" : "#10B981",
    line: isDark ? "#A78BFA" : "#6366F1",
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    const depense = payload.find((item) => item.dataKey === "depense")?.value || 0;
    const recette = payload.find((item) => item.dataKey === "recette")?.value || 0;
    const solde = payload.find((item) => item.dataKey === "solde")?.value ?? null;

    return (
      <div
        className={[
          "rounded-2xl border px-3 py-2 text-xs shadow-sm",
          isDark
            ? "border-slate-700 bg-slate-950 text-slate-100"
            : "border-slate-200 bg-white text-slate-800",
        ].join(" ")}
      >
        <div className="mb-1 text-[11px] font-bold uppercase tracking-[0.16em] opacity-70">
          {label}
        </div>
        <div>
          Dépenses : <strong>{fmtEUR(depense)}</strong>
        </div>
        <div>
          Recettes : <strong>{fmtEUR(recette)}</strong>
        </div>
        {solde !== null && (
          <div>
            Solde cumulé : <strong>{fmtEUR(solde)}</strong>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-5">
      <section className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-800">
          <h3 className="text-base font-bold text-slate-900 dark:text-white">
            Flux mensuels & solde cumulé
          </h3>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Les dépenses et recettes sont affichées par mois, avec une courbe du
            solde cumulé.
          </p>
        </div>

        <div className="h-[380px] p-3">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={monthly}
              margin={{ top: 18, right: 18, left: 8, bottom: 34 }}
            >
              <defs>
                <linearGradient id="accountingDepFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={colors.depTop} stopOpacity={0.95} />
                  <stop offset="100%" stopColor={colors.depBottom} stopOpacity={0.85} />
                </linearGradient>

                <linearGradient id="accountingRecFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={colors.recTop} stopOpacity={0.95} />
                  <stop offset="100%" stopColor={colors.recBottom} stopOpacity={0.85} />
                </linearGradient>
              </defs>

              <CartesianGrid stroke={grid} strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fill: tick, fontSize: 12 }} />
              <YAxis
                tick={{ fill: tick, fontSize: 12 }}
                tickFormatter={(value) => `${Math.round(value / 1000)}k`}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: cursorFill }} />

              <Bar dataKey="depense" name="Dépenses" fill="url(#accountingDepFill)" radius={[8, 8, 0, 0]}>
                <LabelList
                  dataKey="depense"
                  position="top"
                  formatter={(value) => (value ? fmtEUR(value) : "")}
                  style={{ fill: tick, fontSize: 10 }}
                />
              </Bar>

              <Bar dataKey="recette" name="Recettes" fill="url(#accountingRecFill)" radius={[8, 8, 0, 0]}>
                <LabelList
                  dataKey="recette"
                  position="top"
                  formatter={(value) => (value ? fmtEUR(value) : "")}
                  style={{ fill: tick, fontSize: 10 }}
                />
              </Bar>

              <Line
                type="monotone"
                dataKey="solde"
                name="Solde cumulé"
                stroke={colors.line}
                strokeWidth={3}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </section>

      <div className="grid gap-5 xl:grid-cols-2">
        <PieCard
          title="Répartition des dépenses"
          tone="rose"
          data={pieDepenses}
          isDark={isDark}
        />

        <PieCard
          title="Répartition des recettes"
          tone="emerald"
          data={pieRecettes}
          isDark={isDark}
        />
      </div>
    </div>
  );
}

function PieCard({ title, data, tone, isDark }) {
  const textTone =
    tone === "rose"
      ? "text-rose-600 dark:text-rose-300"
      : "text-emerald-600 dark:text-emerald-300";

  return (
    <section className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-800">
        <h3 className={`text-base font-bold ${textTone}`}>{title}</h3>
      </div>

      <div className="h-[360px] p-3">
        {data.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm font-semibold text-slate-400">
            Aucune donnée
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Tooltip
                formatter={(value) => fmtEUR(value)}
                contentStyle={{
                  background: isDark ? "#0f172a" : "#ffffff",
                  border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
                  borderRadius: 14,
                }}
              />

              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={110}
                labelLine={false}
                label={(entry) =>
                  `${entry.name} (${(entry.percent * 100).toFixed(0)}%)`
                }
              >
                {data.map((_, index) => (
                  <Cell
                    key={index}
                    fill={PIE_COLORS[index % PIE_COLORS.length]}
                  />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
    </section>
  );
}